/**
 * transformData applies a full transformation pipeline to raw dataset rows.
 *
 * Pipeline order:
 * 1. Filter
 * 2. Derive
 * 3. Group
 * 4. Aggregate
 * 5. Sort
 * 6. Select
 * 7. Limit
 */
export default function transformData(data, transformations = {}) {
  let result = [...data];
  result = applyFilters(
    result,
    transformations.filters || transformations.filter || []
  );

  result = applyDerives(
    result,
    transformations.derives || transformations.derive || []
  );

  const groups =
    transformations.groups || transformations.group || [];

  const aggregates =
    transformations.aggregates || transformations.aggregate || [];

  if (groups.length && aggregates.length) {
    result = applyGroupsAndAggregates(
      result,
      groups,
      aggregates
    );
  }

  result = applySorts(
    result,
    transformations.sorts || transformations.sort || []
  );

  result = applySelect(
    result,
    transformations.selects || transformations.select || []
  );

  result = applyLimits(
    result,
    transformations.limits || transformations.limit || []
  );

  return result;
}


/**
 * Filters dataset based on conditions.
 *
 * Supported ops:
 * eq, ne, gt, gte, lt, lte, contains, in, between
 */
function applyFilters(data, filters = []) {
  let result = data;

  for (const filter of filters) {
    result = result.filter(row => {
      const value = row[filter.field];

      switch (filter.op) {
        case "eq":
          return value === filter.value;

        case "ne":
          return value !== filter.value;

        case "gt":
          return value > filter.value;

        case "gte":
          return value >= filter.value;

        case "lt":
          return value < filter.value;

        case "lte":
          return value <= filter.value;

        case "contains":
          return String(value).includes(filter.value);

        case "in":
          return Array.isArray(filter.value)
            ? filter.value.includes(value)
            : false;

        case "between":
          return (
            Array.isArray(filter.value) &&
            value >= filter.value[0] &&
            value <= filter.value[1]
          );

        default:
          return true;
      }
    });
  }

  return result;
}


/**
 * Creates derived fields using expressions evaluated against each row.
 *
 * Example:
 * {
 *   field: "double_score",
 *   expression: "score * 2"
 * }
 */
function applyDerives(data, derives = []) {
  return data.map(row => {
    const copy = { ...row };

    for (const derive of derives) {
      copy[derive.field] = evaluateExpression(
        derive.expression,
        copy
      );
    }

    return copy;
  });
}


/**
 * Evaluates a simple JS expression using row values as variables.
 */
function evaluateExpression(expression, row) {
  try {
    return Function(
      ...Object.keys(row),
      `return (${expression});`
    )(...Object.values(row));
  } catch (err) {
    console.error(
      `Failed to evaluate expression: ${expression}`,
      err
    );
    return null;
  }
}


/**
 * Groups data and applies aggregations.
 *
 * Supported group formats:
 *
 * groups: [
 *   { field: "category" }
 * ]
 *
 * OR
 *
 * groups: [
 *   { field: "category" },
 *   { field: "region" }
 * ]
 *
 * OR (legacy)
 *
 * groups: [
 *   { by: ["category", "region"] }
 * ]
 */
function applyGroupsAndAggregates(
  data,
  groups = [],
  aggregates = []
) {
  let groupBy = [];

  if (groups.length) {
    if (Array.isArray(groups[0].by)) {
      // Legacy support
      groupBy = groups[0].by;
    } else {
      // Preferred format
      groupBy = groups
        .map(g => g.field)
        .filter(Boolean);
    }
  }

  if (!groupBy.length) {
    return data;
  }

  const grouped = {};

  for (const row of data) {
    const key = groupBy
      .map(field => row[field])
      .join("|");

    if (!grouped[key]) {
      grouped[key] = [];
    }

    grouped[key].push(row);
  }

  const result = [];

  for (const rows of Object.values(grouped)) {
    const output = {};

    for (const field of groupBy) {
      output[field] = rows[0][field];
    }

    for (const agg of aggregates) {
      const values = rows.map(r => r[agg.field]);

      switch (agg.op) {
        case "sum":
          output[agg.as] = values.reduce(
            (a, b) => Number(a || 0) + Number(b || 0),
            0
          );
          break;

        case "avg":
          output[agg.as] =
            values.reduce(
              (a, b) => Number(a || 0) + Number(b || 0),
              0
            ) / values.length;
          break;

        case "count":
          output[agg.as] = rows.length;
          break;

        case "min":
          output[agg.as] = Math.min(
            ...values.map(v => Number(v))
          );
          break;

        case "max":
          output[agg.as] = Math.max(
            ...values.map(v => Number(v))
          );
          break;

        case "distinct_count":
          output[agg.as] = new Set(values).size;
          break;

        case "first":
          output[agg.as] = values[0];
          break;

        case "last":
          output[agg.as] = values[values.length - 1];
          break;

        default:
          console.warn(
            `Unknown aggregate operation: ${agg.op}`
          );
      }
    }

    result.push(output);
  }

  return result;
}


/**
 * Sorts dataset by field values.
 *
 * Example:
 * {
 *   field: "score",
 *   direction: "asc"
 * }
 */
function applySorts(data, sorts = []) {
  if (!sorts.length) {
    return data;
  }

  const result = [...data];

  for (const sort of [...sorts].reverse()) {
    result.sort((a, b) => {
      const aVal = a[sort.field];
      const bVal = b[sort.field];

      if (aVal === bVal) {
        return 0;
      }

      if (sort.direction === "desc") {
        return aVal < bVal ? 1 : -1;
      }

      return aVal > bVal ? 1 : -1;
    });
  }

  return result;
}


/**
 * Keeps only selected fields.
 *
 * Example:
 * [
 *   {
 *     fields: ["category", "score"]
 *   }
 * ]
 */
function applySelect(data, selects = []) {
  if (!selects.length) {
    return data;
  }

  const fields = selects[0].fields || [];

  return data.map(row => {
    const output = {};

    for (const field of fields) {
      output[field] = row[field];
    }

    return output;
  });
}


/**
 * Limits number of rows returned.
 *
 * Example:
 * [
 *   {
 *     count: 10
 *   }
 * ]
 */
function applyLimits(data, limits = []) {
  if (!limits.length) {
    return data;
  }

  const count = limits[0].count;

  if (typeof count !== "number" || count < 0) {
    return data;
  }

  return data.slice(0, count);
}